import React, { useState, useEffect } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Settings, Trophy, Users, BarChart3, Plus, Save, Eye, EyeOff } from 'lucide-react';

const GamificationAdmin = () => {
  const [settings, setSettings] = useState({
    enabled: false,
    publicLeaderboards: false,
    achievementsEnabled: false
  });
  const [achievements, setAchievements] = useState([]);
  const [platformStats, setPlatformStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('settings');
  const [showNewAchievement, setShowNewAchievement] = useState(false);
  const [newAchievement, setNewAchievement] = useState({
    name: '',
    description: '',
    criteria: {},
    icon: '🏆',
    category: 'general'
  });

  const functions = getFunctions();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);

      // Load settings
      const getSettings = httpsCallable(functions, 'getGamificationSettings');
      const settingsResult = await getSettings();
      if (settingsResult.data.success) {
        setSettings(settingsResult.data.data);
      }

      // Load achievements
      const getAchievements = httpsCallable(functions, 'getAchievementTypes');
      const achievementsResult = await getAchievements();
      if (achievementsResult.data.success) {
        setAchievements(achievementsResult.data.data);
      }

      // Load platform stats
      const getPlatformStats = httpsCallable(functions, 'getPlatformStats');
      const statsResult = await getPlatformStats();
      if (statsResult.data.success) {
        setPlatformStats(statsResult.data.data);
      }

    } catch (error) {
      console.error('Error loading gamification data:', error);
      alert('Error loading data. Please check console.');
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    try {
      setSaving(true);
      const updateSettings = httpsCallable(functions, 'updateGamificationSettings');
      const result = await updateSettings({ settings });
      
      if (result.data.success) {
        alert('Settings saved successfully!');
      } else {
        alert('Error saving settings');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Error saving settings. Please check console.');
    } finally {
      setSaving(false);
    }
  };

  const initSampleData = async () => {
    if (!window.confirm('This will create sample achievements and settings. Continue?')) return;
    
    try {
      const initData = httpsCallable(functions, 'initSampleData');
      const result = await initData();
      
      if (result.data.success) {
        alert(`Sample data initialized! Created ${result.data.achievementsCreated} achievements.`);
        loadData(); // Refresh data
      } else {
        alert('Error initializing sample data');
      }
    } catch (error) {
      console.error('Error initializing sample data:', error);
      alert('Error initializing sample data. Please check console.');
    }
  };

  const createAchievement = async () => {
    try {
      if (!newAchievement.name || !newAchievement.description) {
        alert('Name and description are required');
        return;
      }

      const createAchievementType = httpsCallable(functions, 'createAchievementType');
      const result = await createAchievementType(newAchievement);
      
      if (result.data.success) {
        alert('Achievement created successfully!');
        setShowNewAchievement(false);
        setNewAchievement({
          name: '',
          description: '',
          criteria: {},
          icon: '🏆',
          category: 'general'
        });
        loadData(); // Refresh data
      } else {
        alert('Error creating achievement');
      }
    } catch (error) {
      console.error('Error creating achievement:', error);
      alert('Error creating achievement. Please check console.');
    }
  };

  const addCriteria = (field, value) => {
    setNewAchievement(prev => ({
      ...prev,
      criteria: {
        ...prev.criteria,
        [field]: value
      }
    }));
  };

  const removeCriteria = (field) => {
    const newCriteria = { ...newAchievement.criteria };
    delete newCriteria[field];
    setNewAchievement(prev => ({
      ...prev,
      criteria: newCriteria
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 text-white p-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center">
            <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-purple-500 mx-auto"></div>
            <p className="mt-4 text-gray-400">Loading gamification settings...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white p-6">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-purple-400 mb-2">Gamification Management</h1>
          <p className="text-gray-400">Manage achievements, leaderboards, and engagement features</p>
        </div>

        {/* Status Banner */}
        <div className={`mb-6 p-4 rounded-lg border ${
          settings.enabled 
            ? 'bg-green-900/20 border-green-700 text-green-300' 
            : 'bg-red-900/20 border-red-700 text-red-300'
        }`}>
          <div className="flex items-center gap-3">
            {settings.enabled ? <Eye size={20} /> : <EyeOff size={20} />}
            <span className="font-semibold">
              Gamification is currently {settings.enabled ? 'ENABLED' : 'DISABLED'}
            </span>
            {settings.enabled && (
              <span className="text-sm">
                • Public Leaderboards: {settings.publicLeaderboards ? 'ON' : 'OFF'}
                • Achievements: {settings.achievementsEnabled ? 'ON' : 'OFF'}
              </span>
            )}
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex space-x-1 mb-6 bg-gray-800 p-1 rounded-lg">
          {[
            { id: 'settings', label: 'Settings', icon: Settings },
            { id: 'achievements', label: 'Achievements', icon: Trophy },
            { id: 'stats', label: 'Platform Stats', icon: BarChart3 }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-md transition-colors ${
                activeTab === tab.id
                  ? 'bg-purple-600 text-white'
                  : 'text-gray-400 hover:text-white hover:bg-gray-700'
              }`}
            >
              <tab.icon size={18} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="bg-gray-800 rounded-lg p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold">Gamification Settings</h2>
              <div className="space-x-3">
                <button
                  onClick={initSampleData}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
                >
                  Init Sample Data
                </button>
                <button
                  onClick={saveSettings}
                  disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg transition-colors disabled:opacity-50"
                >
                  <Save size={18} />
                  {saving ? 'Saving...' : 'Save Settings'}
                </button>
              </div>
            </div>

            <div className="space-y-6">
              <div className="flex items-center justify-between p-4 bg-gray-700 rounded-lg">
                <div>
                  <h3 className="font-semibold">Enable Gamification System</h3>
                  <p className="text-sm text-gray-400">Turn on/off all gamification features</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.enabled}
                    onChange={(e) => setSettings(prev => ({ ...prev, enabled: e.target.checked }))}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300/25 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                </label>
              </div>

              <div className="flex items-center justify-between p-4 bg-gray-700 rounded-lg">
                <div>
                  <h3 className="font-semibold">Public Leaderboards</h3>
                  <p className="text-sm text-gray-400">Show leaderboards on marketing website</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.publicLeaderboards}
                    onChange={(e) => setSettings(prev => ({ ...prev, publicLeaderboards: e.target.checked }))}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300/25 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                </label>
              </div>

              <div className="flex items-center justify-between p-4 bg-gray-700 rounded-lg">
                <div>
                  <h3 className="font-semibold">Enable Achievements</h3>
                  <p className="text-sm text-gray-400">Allow musicians to earn achievements</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.achievementsEnabled}
                    onChange={(e) => setSettings(prev => ({ ...prev, achievementsEnabled: e.target.checked }))}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300/25 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                </label>
              </div>
            </div>

            {settings.lastUpdated && (
              <div className="mt-4 text-sm text-gray-500">
                Last updated: {new Date(settings.lastUpdated.seconds * 1000).toLocaleString()}
              </div>
            )}
          </div>
        )}

        {/* Achievements Tab */}
        {activeTab === 'achievements' && (
          <div className="bg-gray-800 rounded-lg p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold">Achievement Management</h2>
              <button
                onClick={() => setShowNewAchievement(true)}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors"
              >
                <Plus size={18} />
                New Achievement
              </button>
            </div>

            {/* Achievement List */}
            <div className="space-y-4">
              {achievements.map(achievement => (
                <div key={achievement.id} className="p-4 bg-gray-700 rounded-lg">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <span className="text-2xl">{achievement.icon}</span>
                      <div>
                        <h3 className="font-semibold">{achievement.name}</h3>
                        <p className="text-gray-400 text-sm">{achievement.description}</p>
                        <div className="mt-2 flex flex-wrap gap-2">
                          <span className="px-2 py-1 bg-gray-600 rounded text-xs">{achievement.category}</span>
                          <span className={`px-2 py-1 rounded text-xs ${
                            achievement.active ? 'bg-green-600' : 'bg-red-600'
                          }`}>
                            {achievement.active ? 'Active' : 'Disabled'}
                          </span>
                        </div>
                        <div className="mt-2 text-xs text-gray-500">
                          Criteria: {JSON.stringify(achievement.criteria)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* New Achievement Form */}
            {showNewAchievement && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
                <div className="bg-gray-800 rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                  <h3 className="text-xl font-semibold mb-4">Create New Achievement</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Name</label>
                      <input
                        type="text"
                        value={newAchievement.name}
                        onChange={(e) => setNewAchievement(prev => ({ ...prev, name: e.target.value }))}
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg"
                        placeholder="Achievement name"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">Description</label>
                      <textarea
                        value={newAchievement.description}
                        onChange={(e) => setNewAchievement(prev => ({ ...prev, description: e.target.value }))}
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg h-20"
                        placeholder="Achievement description"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-1">Icon</label>
                        <input
                          type="text"
                          value={newAchievement.icon}
                          onChange={(e) => setNewAchievement(prev => ({ ...prev, icon: e.target.value }))}
                          className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg"
                          placeholder="🏆"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-1">Category</label>
                        <select
                          value={newAchievement.category}
                          onChange={(e) => setNewAchievement(prev => ({ ...prev, category: e.target.value }))}
                          className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg"
                        >
                          <option value="general">General</option>
                          <option value="milestones">Milestones</option>
                          <option value="performance">Performance</option>
                          <option value="earnings">Earnings</option>
                          <option value="engagement">Engagement</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">Criteria</label>
                      <div className="space-y-2">
                        {Object.entries(newAchievement.criteria).map(([field, value]) => (
                          <div key={field} className="flex items-center gap-2">
                            <span className="text-sm bg-gray-700 px-2 py-1 rounded">{field}</span>
                            <span>=</span>
                            <span className="text-sm bg-gray-700 px-2 py-1 rounded">{value}</span>
                            <button
                              onClick={() => removeCriteria(field)}
                              className="text-red-400 hover:text-red-300"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                        
                        <div className="flex gap-2">
                          <select
                            className="px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg"
                            onChange={(e) => {
                              const field = e.target.value;
                              if (field && !newAchievement.criteria[field]) {
                                addCriteria(field, 1);
                              }
                            }}
                            value=""
                          >
                            <option value="">Add criteria...</option>
                            <option value="totalRequests">Total Requests</option>
                            <option value="totalEarnings">Total Earnings</option>
                            <option value="averageRating">Average Rating</option>
                            <option value="currentMonthRequests">Monthly Requests</option>
                            <option value="repeatCustomers">Repeat Customers</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end gap-3 mt-6">
                    <button
                      onClick={() => setShowNewAchievement(false)}
                      className="px-4 py-2 bg-gray-600 hover:bg-gray-700 rounded-lg transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={createAchievement}
                      className="px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors"
                    >
                      Create Achievement
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Platform Stats Tab */}
        {activeTab === 'stats' && (
          <div className="bg-gray-800 rounded-lg p-6">
            <h2 className="text-xl font-semibold mb-6">Platform Statistics</h2>
            
            {platformStats ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="bg-gray-700 p-4 rounded-lg">
                  <div className="flex items-center gap-3 mb-2">
                    <Users className="text-blue-400" size={24} />
                    <h3 className="font-semibold">Total Musicians</h3>
                  </div>
                  <p className="text-2xl font-bold">{platformStats.totalMusicians}</p>
                  <p className="text-sm text-gray-400">Active this month: {platformStats.activeThisMonth}</p>
                </div>

                <div className="bg-gray-700 p-4 rounded-lg">
                  <div className="flex items-center gap-3 mb-2">
                    <Trophy className="text-yellow-400" size={24} />
                    <h3 className="font-semibold">Total Requests</h3>
                  </div>
                  <p className="text-2xl font-bold">{platformStats.totalRequests.toLocaleString()}</p>
                </div>

                <div className="bg-gray-700 p-4 rounded-lg">
                  <div className="flex items-center gap-3 mb-2">
                    <BarChart3 className="text-green-400" size={24} />
                    <h3 className="font-semibold">Total Earnings</h3>
                  </div>
                  <p className="text-2xl font-bold">${platformStats.totalEarnings.toFixed(2)}</p>
                </div>

                <div className="bg-gray-700 p-4 rounded-lg">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-purple-400 text-xl">⭐</span>
                    <h3 className="font-semibold">Average Rating</h3>
                  </div>
                  <p className="text-2xl font-bold">{platformStats.averageRating.toFixed(2)}</p>
                </div>
              </div>
            ) : (
              <div className="text-center text-gray-400">
                <p>No platform statistics available yet.</p>
                <p className="text-sm mt-2">Stats will appear once musicians start using the system.</p>
              </div>
            )}

            <div className="mt-6 text-sm text-gray-500">
              Last updated: {platformStats?.lastUpdated ? new Date(platformStats.lastUpdated).toLocaleString() : 'Never'}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GamificationAdmin;